import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox, Slider } from 'antd';
import { GRID_SIZES } from '../../../../constants/gridSize';
import { useGridHeatmapConfig } from '../../../../hooks/SimViewer/useGridHeatmapConfig';
import { useThreeDMode } from '../../../../hooks/SimViewer/useThreeDMode';
import { useMapColor } from '../../../../hooks/SimViewer/useMapColor';
import { useMapColorPalette } from '../../../../hooks/SimViewer/useMapColorPalette';
import { InfoPopoverButton } from '../../../InfoPopoverButton';

const GridHeatmapConfig = (): ReactNode => {
  const { t } = useTranslation();
  const { gridSize, setGridSize } = useGridHeatmapConfig();
  const { isThreeDMode, setThreeDMode } = useThreeDMode();
  const { mapColor } = useMapColor();
  const { setMapColorOpacity } = useMapColorPalette();

  return (
    <>
      <Wrapper>
        <InfoPopoverButton
          content={t('additionalInfos.gridHeatmap')}
          style={{ position: 'absolute', top: -14, right: -4 }}
        />
        <SliderContainer>
          <Slider
            min={GRID_SIZES.at(0)?.value || 0}
            max={GRID_SIZES.at(-1)?.value || 0}
            value={gridSize}
            marks={Object.fromEntries(
              GRID_SIZES.map((size) => {
                return [
                  size.value,
                  {
                    style: {
                      transform: 'translate(-16px,6px) rotate(-45deg)',
                      color:
                        gridSize === size.value
                          ? '#122D3A'
                          : 'rgb(210, 210, 210)',
                      fontSize: '0.85em',
                    },
                    label: size.label,
                  },
                ];
              })
            )}
            step={null}
            included={false}
            tooltip={{
              formatter: (value?: number | undefined): string => {
                return (
                  GRID_SIZES.find((size): boolean => {
                    return size.value === value;
                  })?.label || `${value}m`
                );
              },
            }}
            onChange={(value: number): void => {
              setGridSize(value);
            }}
            style={{ marginBottom: '32px' }}
          />
        </SliderContainer>
      </Wrapper>
      <Checkbox
        checked={isThreeDMode}
        style={{ marginLeft: 'auto' }}
        onChange={(event: { target: { checked: boolean } }): void => {
          setThreeDMode(event.target.checked);
          setMapColorOpacity(
            mapColor === 'light'
              ? event.target.checked
                ? 0.6
                : 0.5
              : event.target.checked
                ? 0.8
                : 0.7
          );
        }}
      >
        {t('visualizeInThreeDMode')}
      </Checkbox>
    </>
  );
};

const Wrapper = styled.div`
  position: relative;
  margin-top: 8px;
`;

const SliderContainer = styled.div`
  width: calc(100% - 16px);
  margin: 0 auto;
`;

export { GridHeatmapConfig };
